import { useEffect, useRef, useState } from 'react';

import { useDebouncedValue } from './useDebouncedValue';
import { useUrlPatch } from './useUrlPatch';

interface UrlSearchOptions {
  /** Also drop the `page` param when the settled term changes. */
  resetPage?: boolean;
  /** The page param `resetPage` drops (default `page`; e.g. `zpage`). */
  pageKey?: string;
  /** Debounce delay in ms before the term is written to the URL. */
  delay?: number;
}

interface UrlSearchState {
  /** The live input value — bind this to the search field. */
  value: string;
  /** Update the input value (typing). */
  setValue: (v: string) => void;
  /** The settled, URL-backed term — use THIS as the fetch dependency. */
  committed: string;
}

/**
 * Search-box state whose source of truth is the URL (`?key=`), with a local
 * input mirror for responsiveness. Two-way synced:
 *
 *   type ──▶ input ──debounce──▶ URL          (user typing)
 *   URL change (Back / jump) ──▶ input          (external resync)
 *
 * The external resync is what fixes the "stale box" bug: a plain
 * `useState(searchParams.get(key))` seeds the box only at mount, so any URL
 * change without a remount (section switch, Back/forward, a programmatic
 * patch) leaves the box showing text that no longer matches the list.
 *
 * `lastWritten` records the value WE last pushed/synced, so the resync effect
 * only fires on a genuinely external change and never clobbers active typing.
 * The write effect additionally bails when `debounced` no longer reflects the
 * current input, so a stale debounce timer that fires just after an external
 * change cannot re-clobber the URL with the old term.
 *
 * Returns a named object (not a tuple) so `committed` — the fetch key — can't
 * be grabbed positionally by mistake and silently reintroduce un-debounced
 * fetching.
 */
export function useUrlSearch(
  key = 'q',
  options: UrlSearchOptions = {}
): UrlSearchState {
  const { resetPage = false, pageKey = 'page', delay = 300 } = options;
  const { searchParams, patch } = useUrlPatch();

  const urlValue = searchParams.get(key) ?? '';
  const [input, setInput] = useState(urlValue);
  const debounced = useDebouncedValue(input.trim(), delay);
  const lastWritten = useRef(urlValue);

  // Settled input → URL.
  useEffect(() => {
    // A stale timer must not overwrite an externally-reset input.
    if (debounced !== input.trim()) return;
    if (debounced === (searchParams.get(key) ?? '')) return;
    lastWritten.current = debounced;
    patch((params) => {
      if (debounced) params.set(key, debounced);
      else params.delete(key);
      if (resetPage) params.delete(pageKey);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  // URL → input, but only on an external change (not our own write).
  useEffect(() => {
    if (urlValue !== lastWritten.current) {
      setInput(urlValue);
      lastWritten.current = urlValue;
    }
  }, [urlValue]);

  return { value: input, setValue: setInput, committed: urlValue };
}
